import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Film, Loader2, Plus, Search } from 'lucide-react';
import Sheet from './Sheet';
import DemoOverlay from './DemoOverlay';
import { Chip } from './parts';
import { resolveDemo } from './demoRegistry';
import { safeMediaUrl } from './playerLogic';
import { exerciseLibrary } from '../../services/api';

// «Adicionar exercício»: procurar na biblioteca do ginásio e acrescentar ao treino que está a decorrer um exercício que o
// plano não trazia. Fica no FIM e SEM prescrição — não se inventam séries, repetições nem cargas; regista-se o que se fizer.
// O plano do sócio não muda. Aparece desativado o que o ginásio não consegue servir e o que já está no treino (com o motivo),
// para ninguém andar a tocar em botões que o servidor vai recusar.

const PAGE_SIZE = 20;
const DEBOUNCE_MS = 350;

/** A ficha da biblioteca → o formato que a demonstração consome (igual ao «Substituir exercício»). */
const libraryToDemoSource = (detail) => ({
  name: detail.name,
  muscles: detail.muscles || [],
  equipment: detail.equipmentLinks || [],
  media: detail.media || [],
  steps: detail.steps || [],
  mistakes: detail.mistakes || [],
  tip: detail.tip || null,
  imageUrl: detail.imageUrl, videoUrl: detail.videoUrl, gifUrl: detail.gifUrl,
});

export default function AddExerciseSheet({
  open, onClose, onAdd, busy, inSessionIds = [],
  loadSearch = (params) => exerciseLibrary.search(params),
  loadDetail = (id) => exerciseLibrary.get(id),
  demoFor: demoForProp,
  // O MESMO painel serve o treino em curso e o PLANO do atleta: o que muda é o texto e, no plano, a prescrição —
  // o servidor exige séries e repetições para um exercício do plano (no treino em curso ele entra sem prescrição).
  subtitle, footerNote, withPrescription = false,
}) {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [details, setDetails] = useState({});      // id → { loading, error, demo, muscles, equipment, defaults }
  const [rx, setRx] = useState({ sets: '', repsMin: '', repsMax: '' });
  const [demoFor, setDemoFor] = useState(null);
  const [message, setMessage] = useState(null);
  const seq = useRef(0);

  const already = useMemo(() => new Set(inSessionIds.filter((x) => Number.isInteger(x))), [inSessionIds]);

  useEffect(() => {
    if (open) { setQ(''); setSelected(null); setMessage(null); setDetails({}); setRx({ sets: '', repsMin: '', repsMax: '' }); setDemoFor(null); }
  }, [open]);

  const search = useCallback(async (term) => {
    const mine = ++seq.current;
    setSearching(true);
    setSearchError(null);
    try {
      const res = await loadSearch({ q: term.trim() || undefined, page: 1, pageSize: PAGE_SIZE, sort: 'name' });
      if (mine !== seq.current) return;                       // resposta atrasada de uma pesquisa antiga: ignora-se
      setItems(res?.data?.items || []);
    } catch {
      if (mine !== seq.current) return;
      setItems([]);
      setSearchError('Não foi possível carregar a biblioteca. Verifique a ligação e tente outra vez.');
    } finally {
      if (mine === seq.current) setSearching(false);
    }
  }, [loadSearch]);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => search(q), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [open, q, search]);

  /**
   * A prescrição começa no que o exercício da biblioteca indica e o atleta ajusta. Sem indicação na biblioteca,
   * começa numa prescrição simples e VISÍVEL (3 × 10) — não é recomendação nenhuma, é o ponto de partida editável;
   * o servidor recusaria séries e repetições vazias.
   */
  const prefillRx = (defaults) => {
    if (!defaults) return;
    setRx((cur) => ({
      sets: cur.sets || String(defaults.sets ?? 3),
      repsMin: cur.repsMin || String(defaults.repsMin ?? 10),
      repsMax: cur.repsMax || String(defaults.repsMax ?? defaults.repsMin ?? 10),
    }));
  };

  const fetchDetail = async (id) => {
    if (details[id]?.demo || details[id]?.loading) return;
    setDetails((d) => ({ ...d, [id]: { loading: true } }));
    try {
      const res = await loadDetail(id);
      const source = libraryToDemoSource(res.data);
      const defaults = { sets: res.data.defaultSets ?? null, repsMin: res.data.defaultRepsMin ?? null, repsMax: res.data.defaultRepsMax ?? null };
      setDetails((d) => ({ ...d, [id]: { demo: resolveDemo({ name: res.data.name }, source), muscles: source.muscles, equipment: source.equipment, defaults } }));
      if (withPrescription) prefillRx(defaults);
    } catch {
      setDetails((d) => ({ ...d, [id]: { error: true } }));
    }
  };

  const select = (item) => {
    if (already.has(item.id) || item.available === false) return;
    setSelected(item.id);
    setMessage(null);
    fetchDetail(item.id);
  };

  const sets = Number(rx.sets);
  const repsMin = Number(rx.repsMin);
  const repsMax = Number(rx.repsMax);
  const rxValid = Number.isInteger(sets) && sets > 0 && Number.isInteger(repsMin) && repsMin > 0;

  const confirm = async () => {
    const item = items.find((i) => i.id === selected);
    if (!item) return;
    // Só no plano: a prescrição que vai no corpo do pedido, com os campos que o servidor aceita.
    const prescription = withPrescription
      ? { setsTarget: sets, repsMin, repsMax: Number.isInteger(repsMax) && repsMax >= repsMin ? repsMax : repsMin }
      : undefined;
    const result = await onAdd(item.id, prescription);
    if (!result?.ok) setMessage(result?.message || 'Não foi possível acrescentar o exercício.');
  };

  const chosen = items.find((i) => i.id === selected);
  const demo = demoFor != null ? details[demoFor]?.demo : null;
  const showDemoProp = demoForProp != null ? demoForProp : demoFor;

  return (
    <>
      <Sheet open={open} onClose={busy ? undefined : onClose} title="Adicionar exercício" subtitle={subtitle ?? 'Do que o plano não trazia · só neste treino'} testId="add-exercise-sheet" dismissible={!busy}
        footer={(
          <div className="wp-sub-foot">
            {message ? <p className="wp-error" role="alert" data-testid="add-error">{message}</p> : null}
            <button type="button" className="wp-btn wp-btn-primary wp-btn-xl" onClick={confirm} disabled={!chosen || busy || (withPrescription && !rxValid)} data-testid="add-confirm">
              {busy ? <Loader2 size={20} className="wp-spin" aria-hidden="true" /> : <Plus size={20} aria-hidden="true" />}
              {chosen ? `Acrescentar ${chosen.name}` : 'Escolha um exercício'}
            </button>
            <p className="wp-note wp-center">{footerNote ?? 'Fica no fim do treino, sem séries previstas. O seu plano não é alterado.'}</p>
          </div>
        )}>

        <div className="wp-add-search">
          <Search size={17} aria-hidden="true" />
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Procurar por nome, músculo ou equipamento"
            aria-label="Procurar na biblioteca" data-testid="add-search" autoComplete="off" enterKeyHint="search" />
          {searching ? <Loader2 size={16} className="wp-spin" aria-hidden="true" data-testid="add-searching" /> : null}
        </div>

        {searchError ? <p className="wp-error" role="alert" data-testid="add-search-error">{searchError}</p> : null}

        {!searchError && !searching && items.length === 0 ? (
          <div className="wp-state-center wp-sub-empty" data-testid="add-empty">
            <Search size={30} aria-hidden="true" />
            <p className="wp-demo-empty-title">{q.trim() ? `Nada encontrado para «${q.trim()}»` : 'A biblioteca deste ginásio está vazia'}</p>
            <p className="wp-muted">Tente outro nome. Só aparecem exercícios que o ginásio consegue servir.</p>
          </div>
        ) : null}

        {items.length > 0 ? (
          <ul className="wp-alts" role="radiogroup" aria-label="Exercícios da biblioteca">
            {items.map((item) => {
              const on = item.id === selected;
              const info = details[item.id];
              const img = safeMediaUrl(item.thumbUrl) || safeMediaUrl(item.imageUrl);
              const here = already.has(item.id);
              const off = here || item.available === false;
              return (
                <li key={item.id} className={`wp-alt ${on ? 'is-on' : ''} ${off ? 'is-off' : ''}`} data-testid={`add-result-${item.id}`}>
                  <button type="button" role="radio" aria-checked={on} className="wp-alt-main" onClick={() => select(item)} aria-disabled={off}>
                    <span className="wp-alt-thumb">{img ? <img src={img} alt="" loading="lazy" /> : <Plus size={20} aria-hidden="true" />}</span>
                    <span className="wp-alt-text">
                      <span className="wp-alt-name">{item.name}</span>
                      <span className="wp-alt-meta">{item.groupLabel || 'Exercício'}{item.levelLabel ? ` · ${item.levelLabel}` : ''}</span>
                      <span className="wp-alt-tags">
                        {item.assetState === 'demo' || item.assetState === 'complete' ? <Chip tone="accent" icon={Film}>Como se faz</Chip> : null}
                        {here ? <Chip tone="warn">Já está neste treino</Chip> : null}
                        {!here && item.available === false ? <Chip tone="warn">Equipamento indisponível</Chip> : null}
                        {(item.equipmentLinks || []).slice(0, 2).map((e) => <Chip key={e.id ?? e.name}>{e.name}</Chip>)}
                      </span>
                    </span>
                    <span className="wp-alt-tick" aria-hidden="true">{on ? <Check size={20} strokeWidth={3} /> : null}</span>
                  </button>

                  {on ? (
                    <div className="wp-alt-detail" data-testid="add-detail">
                      {info?.loading ? <p className="wp-muted"><Loader2 size={14} className="wp-spin" aria-hidden="true" /> A carregar detalhes…</p> : null}
                      {info?.error ? <p className="wp-muted">Não foi possível carregar os detalhes. Pode acrescentar na mesma.</p> : null}
                      {info?.demo ? (
                        <>
                          {(info.muscles || []).length > 0 ? (
                            <div className="wp-chip-row"><span className="wp-chip-label">Músculos</span>{info.muscles.slice(0, 5).map((m) => <Chip key={m.id ?? m.name} tone={m.role === 'primary' ? 'accent' : 'plain'}>{m.name}</Chip>)}</div>
                          ) : null}
                          {(info.equipment || []).length > 0 ? (
                            <div className="wp-chip-row"><span className="wp-chip-label">Equipamento</span>{info.equipment.map((e) => <Chip key={e.id ?? e.name}>{e.name}</Chip>)}</div>
                          ) : null}
                          <button type="button" className="wp-btn wp-btn-ghost" onClick={() => setDemoFor(item.id)} data-testid="add-demo"><Film size={16} aria-hidden="true" /> {info.demo.kind === 'video' ? 'Ver vídeo' : 'Ver demonstração'}</button>
                        </>
                      ) : null}

                      {/* No PLANO, a prescrição é obrigatória no servidor: começa no que a biblioteca indica e o atleta
                          ajusta antes de acrescentar. Nada é aplicado sem ele ver os números. */}
                      {withPrescription ? (
                        <div className="wp-add-presc" data-testid="add-prescription">
                          <label htmlFor={`add-sets-${item.id}`}>Séries
                            <input id={`add-sets-${item.id}`} type="number" inputMode="numeric" min="1" step="1" value={rx.sets}
                              onChange={(e) => setRx((r) => ({ ...r, sets: e.target.value }))} data-testid="add-sets" />
                          </label>
                          <label htmlFor={`add-reps-${item.id}`}>Repetições
                            <input id={`add-reps-${item.id}`} type="number" inputMode="numeric" min="1" step="1" value={rx.repsMin}
                              onChange={(e) => setRx((r) => ({ ...r, repsMin: e.target.value }))} data-testid="add-reps-min" />
                          </label>
                          <label htmlFor={`add-reps-max-${item.id}`}>Até
                            <input id={`add-reps-max-${item.id}`} type="number" inputMode="numeric" min="1" step="1" value={rx.repsMax}
                              onChange={(e) => setRx((r) => ({ ...r, repsMax: e.target.value }))} data-testid="add-reps-max" />
                          </label>
                          <p className="wp-note wp-add-presc-note">
                            Começa com a indicação do exercício na biblioteca (3 × 10 quando ela não indica nada). Ajuste ao que vai fazer — é isto que fica guardado no plano.
                          </p>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}
      </Sheet>

      <DemoOverlay open={showDemoProp != null} demo={demo} onClose={() => setDemoFor(null)} stacked />
    </>
  );
}
